import { useQuery } from "@tanstack/react-query";
import { HistoryIcon, SettingsIcon, type LucideIcon } from "lucide-react";
import { useCallback } from "react";

import { meQuery } from "../api/queries";
import { navigateToWorkspace } from "../api/client";
import { AuthenticatedShell } from "../components/AuthenticatedShell";
import type { ShellNavigationItem } from "../components/PrimaryNavigation";
import { Card, CardContent } from "../components/ui/card";
import { resolveShellOrganizationId } from "../lib/organization-context";
import { accountRoute, auditTrailRoute } from "../router";

type SectionContent = {
  activeItem: ShellNavigationItem | null;
  description: string;
  emptyDescription: string;
  emptyTitle: string;
  eyebrow: string;
  icon: LucideIcon;
  title: string;
};

function WorkspaceRedirect({ organizationId, path }: { organizationId: string; path: string }) {
  const redirect = useCallback(
    (node: HTMLSpanElement | null) => {
      if (node) navigateToWorkspace(organizationId, path);
    },
    [organizationId, path],
  );
  return <span hidden ref={redirect} />;
}

function SectionPage({
  content,
  organizationId,
  onOrganizationChange,
  requiresAdminView = false,
}: {
  content: SectionContent;
  organizationId: string | undefined;
  onOrganizationChange: (organizationId: string) => void;
  requiresAdminView?: boolean;
}) {
  const me = useQuery(meQuery);
  const selectedOrganizationId = resolveShellOrganizationId(me.data, organizationId);
  const Icon = content.icon;

  if (me.data && selectedOrganizationId && organizationId !== selectedOrganizationId) {
    const segment = content.activeItem === "audit-trail" ? "audit-trail" : "account";
    return (
      <WorkspaceRedirect
        organizationId={selectedOrganizationId}
        path={`/${segment}?org=${encodeURIComponent(selectedOrganizationId)}`}
      />
    );
  }

  return (
    <AuthenticatedShell
      activeNavigationItem={content.activeItem}
      activeOrganizationId={selectedOrganizationId}
      onOrganizationChange={onOrganizationChange}
      requiresAdminView={requiresAdminView}
    >
      <main className="workspace-main mx-auto w-full max-w-[1240px]" id="main-content">
        <header className="max-w-3xl border-b pb-8">
          <p className="mono-label mb-3">{content.eyebrow}</p>
          <h1 className="display-title mb-3">{content.title}</h1>
          <p className="m-0 max-w-2xl text-muted-foreground">{content.description}</p>
        </header>

        <Card className="mt-8 max-w-3xl">
          <CardContent className="grid min-h-64 place-content-center justify-items-center gap-4 px-6 py-12 text-center">
            <div className="grid size-12 place-items-center rounded-sm border-[1.5px] border-primary bg-info-background text-primary">
              <Icon aria-hidden="true" className="size-5" />
            </div>
            <div className="max-w-md">
              <h2 className="m-0 font-heading text-xl font-bold">{content.emptyTitle}</h2>
              <p className="mt-2 mb-0 text-muted-foreground">{content.emptyDescription}</p>
            </div>
          </CardContent>
        </Card>
      </main>
    </AuthenticatedShell>
  );
}

export function AuditTrailPage() {
  const { org } = auditTrailRoute.useSearch();

  return (
    <SectionPage
      content={{
        activeItem: "audit-trail",
        description:
          "Review important activity across the selected workspace and its private tools.",
        emptyDescription:
          "Auditable platform events will appear here once the event view is connected.",
        emptyTitle: "No audit events to show yet.",
        eyebrow: "Workspace activity",
        icon: HistoryIcon,
        title: "Audit trail",
      }}
      organizationId={org}
      requiresAdminView
      onOrganizationChange={(organizationId) => {
        navigateToWorkspace(
          organizationId,
          `/audit-trail?org=${encodeURIComponent(organizationId)}`,
        );
      }}
    />
  );
}

export function AccountSettingsPage() {
  const { org } = accountRoute.useSearch();

  return (
    <SectionPage
      content={{
        activeItem: null,
        description: "Manage the small set of personal preferences attached to your CCBox account.",
        emptyDescription: "Personal account controls will be added here in the next settings pass.",
        emptyTitle: "Account settings are coming next.",
        eyebrow: "Your profile",
        icon: SettingsIcon,
        title: "Account settings",
      }}
      organizationId={org}
      onOrganizationChange={(organizationId) => {
        navigateToWorkspace(organizationId, `/account?org=${encodeURIComponent(organizationId)}`);
      }}
    />
  );
}
